import openai from './openaiClient';
import {
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error information with isInternal flag and message.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Analyzes candidate profile against job opportunity and returns match percentage with detailed reasoning.
 * @param {Object} candidateProfile - Candidate profile data from onboarding.
 * @param {Object} jobOpportunity - Job opportunity data.
 * @returns {Promise<Object>} Match analysis with percentage and breakdown.
 */
export async function analyzeCandidateMatch(candidateProfile, jobOpportunity) {
  try {
    const prompt = `You are an expert executive recruiter analyzing candidate-to-job fit for senior-level positions.

Candidate Profile:
- Experience Level: ${candidateProfile?.experience || candidateProfile?.seniority || 'Not specified'}
- Years of Experience: ${candidateProfile?.yearsOfExperience || 'Not specified'}
- Industry: ${candidateProfile?.industry || 'Not specified'}
- Location: ${candidateProfile?.location || 'Not specified'}
- Work Mode Preferences: ${candidateProfile?.workMode || candidateProfile?.selectedWorkModes?.join(', ') || 'Not specified'}
- Language: ${candidateProfile?.language || candidateProfile?.selectedLanguage || 'Not specified'}
- Management Level: ${candidateProfile?.managementLevel || 'Not specified'}
- Team Size Managed: ${candidateProfile?.teamSize || 'Not specified'}
- Direct Reports: ${candidateProfile?.directReports || 'Not specified'}
- Business Responsibilities: ${candidateProfile?.responsibilities?.join(', ') || 'Not specified'}
- Budget Range: ${candidateProfile?.budgetRange || 'Not specified'}
- Revenue Impact: ${candidateProfile?.revenueImpact || 'Not specified'}
- Skills: ${candidateProfile?.skills?.join(', ') || candidateProfile?.primarySkills?.join(', ') || 'Not specified'}
- Industry-Specific Track: ${candidateProfile?.techTrack || candidateProfile?.constructionTrack || candidateProfile?.realEstateTrack || 'Not specified'}
- Specializations: ${candidateProfile?.architectureExperience || candidateProfile?.projectTypes?.join(', ') || candidateProfile?.propertyTypes?.join(', ') || 'Not specified'}

Job Opportunity:
- Role Title: ${jobOpportunity?.roleTitle}
- Company Description: ${jobOpportunity?.companyDescription}
- Location: ${jobOpportunity?.location}
- Work Mode: ${jobOpportunity?.workMode}
- Team Size: ${jobOpportunity?.teamSize}
- Growth Stage: ${jobOpportunity?.growthStage}
- Employee Count: ${jobOpportunity?.employeeCount || 'Not specified'}
- Compensation: ${jobOpportunity?.compensationRange}
- Requirements: ${jobOpportunity?.requirements?.join(' | ')}
- Benefits: ${jobOpportunity?.benefits?.join(', ')}

Analyze the match between this candidate and job opportunity. Consider:
1. Experience level alignment (seniority, years, industry)
2. Skills and technical expertise match
3. Management scope compatibility (team size, budget, responsibilities)
4. Location and work mode preferences
5. Language requirements (especially for bilingual roles)
6. Industry-specific qualifications and track record
7. Company stage and scale fit

Provide your analysis in the following structure:
- Overall match percentage (0-100)
- Brief match summary (2-3 sentences)
- Key strengths (3-5 bullet points)
- Potential concerns (2-3 bullet points, or "None" if excellent fit)
- Recommendation (Strong Match / Good Match / Moderate Match / Weak Match)`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { 
          role: 'system', 
          content: 'You are an expert executive recruiter with deep experience in senior-level talent assessment. Provide honest, balanced evaluations that consider both strengths and potential concerns.' 
        },
        { role: 'user', content: prompt },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'match_analysis',
          schema: {
            type: 'object',
            properties: {
              matchPercentage: { 
                type: 'number',
                description: 'Overall match score from 0-100'
              },
              matchSummary: { 
                type: 'string',
                description: 'Brief 2-3 sentence summary of the match quality'
              },
              keyStrengths: { 
                type: 'array', 
                items: { type: 'string' },
                description: 'List of 3-5 key strengths that make this a good match'
              },
              potentialConcerns: { 
                type: 'array', 
                items: { type: 'string' },
                description: 'List of 2-3 potential concerns or gaps, or empty array if none'
              },
              recommendation: { 
                type: 'string',
                enum: ['Strong Match', 'Good Match', 'Moderate Match', 'Weak Match'],
                description: 'Overall recommendation category'
              }
            },
            required: ['matchPercentage', 'matchSummary', 'keyStrengths', 'potentialConcerns', 'recommendation'],
            additionalProperties: false,
          },
        },
      },
      reasoning_effort: 'medium',
      verbosity: 'medium',
    });

    const analysis = JSON.parse(response?.choices?.[0]?.message?.content);
    return analysis;
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in candidate match analysis:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Batch analyzes multiple candidates against a single job opportunity.
 * @param {Array<Object>} candidates - Array of candidate profiles.
 * @param {Object} jobOpportunity - Job opportunity data.
 * @returns {Promise<Array<Object>>} Array of match analyses with candidate IDs.
 */
export async function batchAnalyzeCandidates(candidates, jobOpportunity) {
  try {
    const analyses = await Promise.all(
      candidates?.map(async (candidate) => {
        try {
          const analysis = await analyzeCandidateMatch(candidate, jobOpportunity);
          return {
            candidateId: candidate?.id,
            ...analysis
          };
        } catch (error) {
          // Return fallback for individual failures
          return {
            candidateId: candidate?.id,
            matchPercentage: 0,
            matchSummary: 'Unable to analyze match at this time.',
            keyStrengths: [],
            potentialConcerns: ['Analysis temporarily unavailable'],
            recommendation: 'Weak Match',
            error: true
          };
        }
      })
    );
    return analyses;
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in batch candidate analysis:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Analyzes multiple job opportunities against a single candidate profile.
 * @param {Object} candidateProfile - Candidate profile data.
 * @param {Array<Object>} opportunities - Array of job opportunities.
 * @returns {Promise<Array<Object>>} Array of match analyses with opportunity IDs.
 */
export async function analyzeOpportunitiesForCandidate(candidateProfile, opportunities) {
  try {
    const analyses = await Promise.all(
      opportunities?.map(async (opportunity) => {
        try {
          const analysis = await analyzeCandidateMatch(candidateProfile, opportunity);
          return {
            opportunityId: opportunity?.id,
            ...analysis
          };
        } catch (error) {
          // Return fallback for individual failures
          return {
            opportunityId: opportunity?.id,
            matchPercentage: 0,
            matchSummary: 'Unable to analyze match at this time.',
            keyStrengths: [],
            potentialConcerns: ['Analysis temporarily unavailable'],
            recommendation: 'Weak Match',
            error: true
          };
        }
      })
    );
    return analyses;
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in opportunity analysis:', error);
    }
    throw new Error(errorInfo.message);
  }
}

export default {
  analyzeCandidateMatch,
  batchAnalyzeCandidates,
  analyzeOpportunitiesForCandidate
};